import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import styles from "./UsersPage.module.scss";
import {
  Buildings,
  Eye,
  IdentificationBadge,
  MagnifyingGlass,
  PencilSimple,
  ShieldCheck,
  Trash,
  Users,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";
import CustomCheckbox from "../components/CustomCheckbox/CustomCheckbox";
import profilePlaceholder from "../assets/profile-placeholder.jpg";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type FilterKey = "all" | "app" | "portal" | "owner" | "team";

interface Organization {
  organization_id: string;
  organization_name: string;
  systemrole?: string | null;
  orgroletype_name?: string | null;
  is_owner?: boolean;
  is_active?: boolean;
}

interface AppAccess {
  app_id: string;
  app_name: string;
}

interface PortalContext {
  portal_id: number | null;
  portal_name: string | null;
  client_organization_id: string | null;
  customer_organization_id: string | null;
  customer_organization_name: string | null;
  contact_name: string | null;
}

interface Classification {
  account_type: string;
  account_type_label: string;
  organization_role_label: string;
  is_system_admin: boolean;
  is_account_owner?: boolean;
  is_company_owner: boolean;
  is_team_user: boolean;
  is_portal_customer: boolean;
  has_app_access: boolean;
  has_org_membership: boolean;
  badges: { key: string; label: string }[];
}

interface ProcessingStatus {
  card_approved: boolean;
  ach_approved: boolean;
  merchant_ready: boolean;
  source_available?: boolean;
  organizations: {
    organization_id: string;
    organization_name: string | null;
    card_approved: boolean;
    ach_approved: boolean;
    merchant_ready: boolean;
    onboarding_status: string | null;
    gateway_status: string | null;
  }[];
}

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  active_organization_name: string | null;
  last_login: string | null;
  organizations: Organization[];
  app_accesses?: AppAccess[];
  portal_contexts?: PortalContext[];
  classification?: Classification;
  activeorganizationid: string | null;
  organization_count?: number;
  owned_organization_count?: number;
  processing_status?: ProcessingStatus;
}

const getUserName = (user: User) =>
  [user.firstname, user.lastname].filter(Boolean).join(" ").trim() || "Unnamed User";

const getPrimaryPortalContext = (user: User) =>
  Array.isArray(user.portal_contexts) && user.portal_contexts.length > 0
    ? user.portal_contexts[0]
    : null;

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "Never";

const getBadgeClass = (key: string) => {
  if (key === "portal_customer") return styles.badgePortal;
  if (key === "account_owner") return styles.badgeOwner;
  if (key === "company_owner") return styles.badgeOwner;
  if (key === "team_user") return styles.badgeTeam;
  if (key === "system_admin") return styles.badgeAdmin;
  return styles.badgeMuted;
};

const normalizeLabel = (value: string | null | undefined) =>
  String(value || "").trim().toLowerCase();

export default function UsersPage() {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [profileImages, setProfileImages] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrgsToDelete, setSelectedOrgsToDelete] = useState<string[]>([]);

  const { setEditUserName, setEditOrganizationName } = useAppContext();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setError("");
      const res = await api.post<{ message: string; users: User[] }>("/core/users/getall");
      setAllUsers(res.data.users || []);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Failed to load users";
      console.error("Failed to load users:", error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfilePictures = async (users: User[]) => {
    const newImages: { [key: string]: string } = {};

    await Promise.all(
      users.map(async (user) => {
        if (profileImages[user.id]) {
          newImages[user.id] = profileImages[user.id];
          return;
        }

        try {
          const res = await api.post("/core/files/get", {
            user_id: user.id,
            file_for: "user_profile_picture",
          });
          newImages[user.id] =
            res.data?.files?.length > 0 && res.data.files[0].signed_url
              ? res.data.files[0].signed_url
              : profilePlaceholder;
        } catch (err: any) {
          console.error(
            `Failed to fetch profile picture for user ${user.id}:`,
            err.response?.data || err.message
          );
          newImages[user.id] = profilePlaceholder;
        }
      })
    );

    setProfileImages((prev) => ({ ...prev, ...newImages }));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const counts = useMemo(() => {
    return allUsers.reduce(
      (acc, user) => {
        const classification = user.classification;
        if (classification?.has_app_access) acc.app += 1;
        if (classification?.is_portal_customer) acc.portal += 1;
        if (classification?.is_account_owner || classification?.is_company_owner) acc.owner += 1;
        if (classification?.is_team_user) acc.team += 1;
        return acc;
      },
      { all: allUsers.length, app: 0, portal: 0, owner: 0, team: 0 }
    );
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return allUsers.filter((user) => {
      const classification = user.classification;
      const matchesFilter =
        activeFilter === "all" ||
        (activeFilter === "app" && classification?.has_app_access) ||
        (activeFilter === "portal" && classification?.is_portal_customer) ||
        (activeFilter === "owner" &&
          (classification?.is_account_owner || classification?.is_company_owner)) ||
        (activeFilter === "team" && classification?.is_team_user);

      if (!matchesFilter) return false;
      if (!query) return true;

      const portalContext = getPrimaryPortalContext(user);
      const haystack = [
        getUserName(user),
        user.email,
        user.active_organization_name,
        classification?.account_type_label,
        classification?.organization_role_label,
        ...(user.organizations || []).map((org) => org.organization_name),
        ...(user.app_accesses || []).map((app) => app.app_name),
        ...(user.portal_contexts || []).flatMap((context) => [
          context.portal_name,
          context.customer_organization_name,
          context.contact_name,
        ]),
        portalContext?.portal_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [activeFilter, allUsers, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const pageStart = (currentPage - 1) * rowsPerPage;
  const paginatedUsers = filteredUsers.slice(pageStart, pageStart + rowsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, rowsPerPage, searchQuery]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    fetchProfilePictures(paginatedUsers);
  }, [filteredUsers, currentPage, rowsPerPage]);

  const handleDeleteUser = (user: User) => {
    setConfirmDeleteUser(user);
    const defaultOrgId = user.activeorganizationid;
    const validOrg = user.organizations?.find(
      (org) => org.organization_id === defaultOrgId
    );
    setSelectedOrgsToDelete(validOrg ? [validOrg.organization_id] : []);
    setShowDeleteModal(true);
  };

  const handleOrgCheckboxChange = (orgId: string, checked: boolean) => {
    setSelectedOrgsToDelete((prev) =>
      checked ? [...prev, orgId] : prev.filter((id) => id !== orgId)
    );
  };

  const confirmDelete = async () => {
    if (!confirmDeleteUser) return;
    try {
      await api.post("/core/users/delete", {
        userId: confirmDeleteUser.id,
        organizationsToDelete: selectedOrgsToDelete,
      });
      await fetchUsers();
    } catch (err) {
      console.error("Delete failed", err);
      setError("Failed to delete user");
    } finally {
      setShowDeleteModal(false);
      setConfirmDeleteUser(null);
      setSelectedOrgsToDelete([]);
    }
  };

  const filterOptions: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "app", label: "App users", count: counts.app },
    { key: "portal", label: "Portal customers", count: counts.portal },
    { key: "owner", label: "Account owners", count: counts.owner },
    { key: "team", label: "Team users", count: counts.team },
  ];

  return (
    <div className={styles.container}>
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.toolbar}>
        <button className={styles.primaryButton} onClick={() => navigate("/users/new")}>
          + Add User
        </button>

        <div className={styles.searchBox}>
          <MagnifyingGlass size={18} />
          <input
            type="text"
            placeholder="Search users, portals, companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.filters}>
        {filterOptions.map((option) => (
          <button
            key={option.key}
            className={activeFilter === option.key ? styles.filterActive : ""}
            onClick={() => setActiveFilter(option.key)}
          >
            <span>{option.label}</span>
            <strong>{option.count}</strong>
          </button>
        ))}
      </div>

      {loading && <LoadingOverlay />}

      {!loading && !error && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access Type</th>
                  <th>Company / Customer Context</th>
                  <th>Apps & Orgs</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const classification = user.classification;
                  const portalContext = getPrimaryPortalContext(user);
                  const portalContextCount = user.portal_contexts?.length || 0;
                  const activeOrg =
                    user.active_organization_name ||
                    user.organizations?.find((org) => org.is_active)?.organization_name ||
                    null;
                  const visibleOrganizations = (user.organizations || []).filter(
                    (org) =>
                      normalizeLabel(org.organization_name) !== normalizeLabel(activeOrg) &&
                      !((user.app_accesses || []).some(
                        (app) => normalizeLabel(app.app_name) === normalizeLabel(org.organization_name)
                      ))
                  );
                  const hasCardApproval = user.processing_status?.card_approved === true;
                  const hasAchApproval = user.processing_status?.ach_approved === true;
                  const processingUnavailable =
                    user.processing_status?.source_available === false &&
                    (classification?.is_account_owner || classification?.is_company_owner);
                  const hasProcessingApproval = hasCardApproval || hasAchApproval;
                  const badges = classification?.badges?.length
                    ? classification.badges
                    : [{ key: "identity", label: "Identity" }];

                  return (
                    <tr key={user.id}>
                      <td>
                        <div className={styles.userCell}>
                          <img
                            src={profileImages[user.id] || profilePlaceholder}
                            alt={`${getUserName(user)} profile`}
                            className={styles.profileImage}
                          />
                          <div>
                            <strong>{getUserName(user)}</strong>
                            <span>{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.badgeStack}>
                          {badges.map((badge) => (
                            <span
                              key={badge.key}
                              className={`${styles.badge} ${getBadgeClass(badge.key)}`}
                            >
                              {badge.key === "system_admin" && (
                                <ShieldCheck size={14} weight="bold" />
                              )}
                              {badge.key === "portal_customer" && (
                                <IdentificationBadge size={14} weight="bold" />
                              )}
                              {(badge.key === "account_owner" ||
                                badge.key === "company_owner") && (
                                <Buildings size={14} weight="bold" />
                              )}
                              {badge.key === "team_user" && <Users size={14} weight="bold" />}
                              {badge.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className={styles.contextCell}>
                          {portalContext ? (
                            <>
                              <strong>Customer of {portalContext.portal_name || "Customer Portal"}</strong>
                              <span>
                                {portalContext.customer_organization_name ||
                                  portalContext.contact_name ||
                                  "Portal contact"}
                                {portalContextCount > 1 ? ` + ${portalContextCount - 1} more` : ""}
                              </span>
                            </>
                          ) : (
                            <>
                              <strong>{activeOrg || "No active company"}</strong>
                              <span>{classification?.organization_role_label || "Identity record"}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={styles.chipGroup}>
                          {(user.app_accesses || []).slice(0, 2).map((app) => (
                            <span key={app.app_id} className={styles.chip}>
                              {app.app_name} app
                            </span>
                          ))}
                          {visibleOrganizations.slice(0, 2).map((org) => (
                            <span key={org.organization_id} className={styles.chipMuted}>
                              {org.organization_name}
                            </span>
                          ))}
                          {(user.app_accesses || []).length + (user.organizations || []).length === 0 && (
                            <span className={styles.emptyText}>Portal only</span>
                          )}
                          {(hasProcessingApproval || processingUnavailable) && (
                            <div className={styles.processingChips}>
                              {hasCardApproval && (
                                <span className={`${styles.processingChip} ${styles.cardApproved}`}>
                                  Card approved
                                </span>
                              )}
                              {hasAchApproval && (
                                <span className={`${styles.processingChip} ${styles.achApproved}`}>
                                  ACH approved
                                </span>
                              )}
                              {processingUnavailable && (
                                <span className={`${styles.processingChip} ${styles.processingUnavailable}`}>
                                  Processing status unavailable
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{formatDate(user.last_login)}</td>
                      <td>
                        <div className={styles.actions}>
                          <button onClick={() => navigate(`/users/view/${user.id}`)} title="View">
                            <Eye size={20} weight="bold" />
                          </button>
                          <button
                            onClick={() => {
                              setEditUserName(getUserName(user));
                              setEditOrganizationName(activeOrg || "");
                              navigate(`/users/edit/${user.id}`);
                            }}
                            title="Edit"
                          >
                            <PencilSimple size={20} weight="bold" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            title="Delete"
                            className={styles.deleteBtn}
                          >
                            <Trash size={20} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {paginatedUsers.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <div className={styles.emptyState}>No users match the current view.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.paginationBar}>
            <span>
              Showing {filteredUsers.length === 0 ? 0 : pageStart + 1}-
              {Math.min(pageStart + rowsPerPage, filteredUsers.length)} of {filteredUsers.length}
            </span>
            <div className={styles.paginationControls}>
              <label>
                Rows
                <select
                  value={rowsPerPage}
                  onChange={(event) => setRowsPerPage(Number(event.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <button disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>
                Previous
              </button>
              <strong>
                Page {currentPage} of {totalPages}
              </strong>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((page) => page + 1)}
              >
                Next
              </button>
            </div>
          </div>

          {showDeleteModal && confirmDeleteUser && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modal}>
                <p>
                  Delete <strong>{getUserName(confirmDeleteUser)}</strong>?
                </p>

                {Array.isArray(confirmDeleteUser.organizations) &&
                  confirmDeleteUser.organizations.length > 0 && (
                    <div className={styles.checkboxList}>
                      {confirmDeleteUser.organizations.map((org) => (
                        <CustomCheckbox
                          key={org.organization_id}
                          label={org.organization_name}
                          checked={selectedOrgsToDelete.includes(org.organization_id)}
                          onChange={(checked) =>
                            handleOrgCheckboxChange(org.organization_id, checked)
                          }
                          id={`delete-org-${org.organization_id}`}
                        />
                      ))}
                    </div>
                  )}

                <div className={styles.modalActions}>
                  <button onClick={confirmDelete} className={styles.confirmBtn}>
                    Delete
                  </button>
                  <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
